import * as React from 'react'

import EditBusinesTripFooter from './EditBusinesTripFooter'
import EditBusinessTripHeader from './EditBusinessTripHeader'
import EditBusinessTripContent from './EditBusinessTripContent'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { useBusinessTripAllowancesSync } from '../hooks/useBusinessTripAllowancesSync'

interface BusinessTripEditFormProps {
  tripData: any
  companyId: string
  driverId: string
  tripId: string
  driverName: string | null
}

const EditBusinessTripContainer: React.FC<BusinessTripEditFormProps> = ({
  tripData,
  companyId,
  driverId,
  tripId,
  driverName,
}) => {
  const [isSaving, setIsSaving] = React.useState(false)
  const [formData, setFormData] = React.useState<any>(tripData)
  const [isDirty, setIsDirty] = React.useState<boolean>(false)

  const [validationErrors, setValidationErrors] = React.useState<
    Record<string, any>
  >({})

  const setDirty = useDirtyStore((s) => s.setDirty)

  const formDataRef = React.useRef(formData)
  formDataRef.current = formData

  const setFormDataDirty = React.useCallback((updater: any) => {
    setIsDirty(true)
    setFormData(updater)
  }, [])

  // Diets and the auto-generated costs are recalculated as soon as the events
  // change, so the Save button persists what the user sees on every tab.
  const { scheduleRecalculation, regenerateNow, flushPending } =
    useBusinessTripAllowancesSync({
      companyId,
      driverId,
      formDataRef,
      setFormData: setFormDataDirty,
    })

  React.useEffect(() => {
    if (tripData) {
      setFormData({
        ...tripData,
        destinationCountry: tripData.destinationCountry || tripData.country,
        events: tripData.events || [],
        allowances: tripData.allowances || [],
        accommodations: tripData.accommodations || tripData.accomodations || [],
        invoices: tripData.invoices || [],
        advancePayments:
          tripData.advancePayments || tripData.advancePayments || [],
        alimentations: tripData.alimentations || [],
      })
      setIsDirty(false)
    }
  }, [tripData])

  React.useEffect(() => {
    setDirty(isDirty)
    return () => setDirty(false)
  }, [setDirty, isDirty])

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <EditBusinessTripHeader
        driverName={driverName}
        isDirty={isDirty}
        companyId={companyId}
        formData={formData}
        setFormData={setFormDataDirty}
      />

      <EditBusinessTripContent
        tripId={tripId}
        driverName={driverName}
        formData={formData}
        setFormData={setFormDataDirty}
        validationErrors={validationErrors}
        companyId={companyId}
        driverId={driverId}
        setValidationErrors={setValidationErrors}
        onEventsChanged={scheduleRecalculation}
        onRegenerateAllowances={regenerateNow}
      />

      <EditBusinesTripFooter
        formData={formData}
        companyId={companyId}
        driverId={driverId}
        setValidationErrors={setValidationErrors}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        validationErrors={validationErrors}
        tripId={tripId}
        flushPendingAllowances={flushPending}
      />
    </div>
  )
}

export default EditBusinessTripContainer
